<template>
	<app-content class="h-panel">
		<Loading text="数据加载中..." :loading="loading"></Loading>
		<div class="h-panel-bar">
			<span class="h-panel-title">合并报表</span>
			<Button class="float-right" @click="$router.push({name:'ReportList'})">返回</Button>
			<Button
				class="float-right margin-right"
				color="green"
				:loading="aiLoading"
				@click="aiSuggestEliminations"
			>
				一键AI抵消
			</Button>
			<Button
				class="float-right margin-right"
				color="primary"
				:loading="previewLoading"
				@click="loadPreview"
			>
				查询/重算
			</Button>
			<Button
				class="float-right margin-right"
				color="green"
				:loading="exportLoading"
				@click="exportExcel"
			>
				导出Excel
			</Button>
			<Button
				class="float-right margin-right"
				@click="openEliminationModal"
			>
				抵消统计
			</Button>
		</div>

		<div class="margin-left margin-right margin-top">
			<account-date-choose v-model="accountDate"/>
		</div>

		<div class="margin-left margin-right margin-top option-panel">
			<div class="option-title">参与账套</div>
			<div class="account-sets-list">
				<label v-for="item in availableAccountSets" :key="item.id" class="account-item">
					<input type="checkbox" :value="item.id" v-model="selectedAccountSetsIds"/>
					<span>{{ item.companyName }}</span>
				</label>
			</div>
		</div>

		<div class="margin-left margin-right margin-top option-panel option-inline">
			<div class="option-block">
				<div class="option-title">主模板账套</div>
				<select class="h-input anchor-select" v-model="anchorAccountSetsId">
					<option v-for="item in selectedAccountSets" :key="item.id" :value="item.id">{{ item.companyName }}</option>
				</select>
			</div>
			<label class="option-check">
				<input type="checkbox" v-model="includeAccountColumns"/>
				<span>包含帐套分列</span>
			</label>
		</div>

		<div class="margin-left margin-right margin-top warn-box" v-if="warnings.length > 0">
			<div class="warn-title">提示</div>
			<div v-for="(warning, idx) in warnings" :key="idx" class="warn-item">{{ warning }}</div>
		</div>

		<div class="h-panel-body" v-if="reports.length > 0">
			<Tabs :datas="tabDatas" v-model="activeReportKey" class="h-tabs-card" />

			<Table :datas="currentItems" border class="small-td margin-top">
				<TableItem title="项目">
					<div slot-scope="{data}" :class="{'font-bold':data.isBolder}" :style="{paddingLeft:((data.level-1)*15)+'px'}">
						{{data.title}}
					</div>
				</TableItem>
				<TableItem title="行次" :width="70">
					<template slot-scope="{data}">
						{{data.isClassified ? '' : (data.lineNum > -1 ? data.lineNum : '')}}
					</template>
				</TableItem>
				<TableItem title="合并本年" align="right">
					<template slot-scope="{data}">{{ data.currentYearAmount | numFormat }}</template>
				</TableItem>
				<TableItem title="合并本期" align="right">
					<template slot-scope="{data}">{{ data.currentPeriodAmount | numFormat }}</template>
				</TableItem>
				<TableItem v-if="showLastYear" title="合并上年" align="right">
					<template slot-scope="{data}">{{ data.lastYearAmount | numFormat }}</template>
				</TableItem>

				<TableItem
					v-for="accountSet in displayAccountColumns"
					:key="'account-column-' + accountSet.id"
					:title="accountSet.companyName"
					align="right"
				>
					<template slot-scope="{data}">
						<div class="account-column-cell">
							<div>本年：{{ getBreakdownAmount(data, accountSet.id, 'currentYearAmount') | numFormat }}</div>
							<div>本期：{{ getBreakdownAmount(data, accountSet.id, 'currentPeriodAmount') | numFormat }}</div>
							<div v-if="showLastYear">上年：{{ getBreakdownAmount(data, accountSet.id, 'lastYearAmount') | numFormat }}</div>
						</div>
					</template>
				</TableItem>

				<TableItem title="抵消本年" align="right">
					<template slot-scope="{data}">
						<input
							class="cell-input"
							:value="formatInput(getEliminationValue(data, 'currentYearAmount'))"
							@input="setEliminationValue(data, 'currentYearAmount', $event.target.value)"
						/>
					</template>
				</TableItem>
				<TableItem title="抵消本期" align="right">
					<template slot-scope="{data}">
						<input
							class="cell-input"
							:value="formatInput(getEliminationValue(data, 'currentPeriodAmount'))"
							@input="setEliminationValue(data, 'currentPeriodAmount', $event.target.value)"
						/>
					</template>
				</TableItem>
				<TableItem v-if="showLastYear" title="抵消上年" align="right">
					<template slot-scope="{data}">
						<input
							class="cell-input"
							:value="formatInput(getEliminationValue(data, 'lastYearAmount'))"
							@input="setEliminationValue(data, 'lastYearAmount', $event.target.value)"
						/>
					</template>
				</TableItem>
				<TableItem v-if="!includeAccountColumns" title="账套明细">
					<template slot-scope="{data}">
						<div class="breakdown-item" v-for="item in data.breakdown" :key="item.accountSetsId">
							{{item.companyName}}：
							<span v-if="item.missing">-</span>
							<span v-else>
								{{item.currentYearAmount | numFormat}} / {{item.currentPeriodAmount | numFormat}}
								<span v-if="showLastYear"> / {{item.lastYearAmount | numFormat}}</span>
							</span>
						</div>
					</template>
				</TableItem>
			</Table>
		</div>

		<Modal v-model="eliminationModal.visible" title="抵消统计" width="94%" hasCloseIcon>
			<div class="elimination-toolbar">
				<div class="elimination-filter">
					<label>报表：</label>
					<select class="h-input elimination-select" v-model="eliminationModal.reportKey">
						<option value="">全部</option>
						<option v-for="item in tabDatas" :key="'modal-report-' + item.key" :value="item.key">{{ item.title }}</option>
					</select>
				</div>
				<div class="elimination-filter">
					<label>项目：</label>
					<input
						class="h-input elimination-input"
						v-model.trim="eliminationModal.keyword"
						placeholder="按项目关键字过滤"
					/>
				</div>
				<label class="option-check elimination-check">
					<input type="checkbox" v-model="eliminationModal.onlyWithElimination"/>
					<span>仅显示有抵消</span>
				</label>
			</div>

			<Table :datas="filteredEliminationRows" border class="small-td">
				<TableItem title="报表" width="140">
					<template slot-scope="{data}">{{ data.reportName }}</template>
				</TableItem>
				<TableItem title="项目" min-width="220">
					<template slot-scope="{data}">
						<div :style="{paddingLeft: ((data.level - 1) * 14) + 'px'}">{{ data.title }}</div>
					</template>
				</TableItem>
				<TableItem title="行次" width="70">
					<template slot-scope="{data}">{{ data.lineNum }}</template>
				</TableItem>

				<TableItem title="本年抵消前" align="right" width="120">
					<template slot-scope="{data}">{{ data.currentYearBefore | numFormat }}</template>
				</TableItem>
				<TableItem title="本年抵消" align="right" width="120">
					<template slot-scope="{data}">
						<input
							class="cell-input"
							:value="formatInput(getEliminationValueByKey(data.reportKey, data.lineKey, 'currentYearAmount', data.currentYearElimination))"
							@input="setEliminationValueByKey(data.reportKey, data.lineKey, 'currentYearAmount', $event.target.value)"
						/>
					</template>
				</TableItem>
				<TableItem title="本年抵消后" align="right" width="120">
					<template slot-scope="{data}">{{ calcAfterAmount(data.currentYearBefore, data.reportKey, data.lineKey, 'currentYearAmount', data.currentYearElimination) | numFormat }}</template>
				</TableItem>

				<TableItem title="本期抵消前" align="right" width="120">
					<template slot-scope="{data}">{{ data.currentPeriodBefore | numFormat }}</template>
				</TableItem>
				<TableItem title="本期抵消" align="right" width="120">
					<template slot-scope="{data}">
						<input
							class="cell-input"
							:value="formatInput(getEliminationValueByKey(data.reportKey, data.lineKey, 'currentPeriodAmount', data.currentPeriodElimination))"
							@input="setEliminationValueByKey(data.reportKey, data.lineKey, 'currentPeriodAmount', $event.target.value)"
						/>
					</template>
				</TableItem>
				<TableItem title="本期抵消后" align="right" width="120">
					<template slot-scope="{data}">{{ calcAfterAmount(data.currentPeriodBefore, data.reportKey, data.lineKey, 'currentPeriodAmount', data.currentPeriodElimination) | numFormat }}</template>
				</TableItem>

				<TableItem v-if="eliminationHasLastYear" title="上年抵消前" align="right" width="120">
					<template slot-scope="{data}">{{ data.lastYearBefore | numFormat }}</template>
				</TableItem>
				<TableItem v-if="eliminationHasLastYear" title="上年抵消" align="right" width="120">
					<template slot-scope="{data}">
						<input
							class="cell-input"
							:value="formatInput(getEliminationValueByKey(data.reportKey, data.lineKey, 'lastYearAmount', data.lastYearElimination))"
							@input="setEliminationValueByKey(data.reportKey, data.lineKey, 'lastYearAmount', $event.target.value)"
						/>
					</template>
				</TableItem>
				<TableItem v-if="eliminationHasLastYear" title="上年抵消后" align="right" width="120">
					<template slot-scope="{data}">{{ calcAfterAmount(data.lastYearBefore, data.reportKey, data.lineKey, 'lastYearAmount', data.lastYearElimination) | numFormat }}</template>
				</TableItem>
			</Table>

			<div slot="footer">
				<Button @click="eliminationModal.visible = false">关闭</Button>
				<Button color="primary" :loading="previewLoading" @click="loadPreview">应用并重算</Button>
			</div>
		</Modal>
	</app-content>
</template>

<script>
import { mapState } from 'vuex'

export default {
	name: 'ReportMergeView',
	data() {
			return {
				loading: false,
				previewLoading: false,
				aiLoading: false,
				exportLoading: false,
				includeAccountColumns: true,
				accountDate: null,
			availableAccountSets: [],
			selectedAccountSetsIds: [],
				anchorAccountSetsId: null,
				reports: [],
				activeReportKey: '',
				warnings: [],
				eliminationMap: {},
				eliminationModal: {
					visible: false,
					reportKey: '',
					keyword: '',
					onlyWithElimination: true
				}
			}
		},
	computed: {
		...mapState(['currentAccountSets']),
		selectedAccountSets() {
			const selectedSet = new Set(this.selectedAccountSetsIds || []);
			return this.availableAccountSets.filter(item => selectedSet.has(item.id));
		},
		tabDatas() {
			return this.reports.map(report => ({ key: report.reportKey, title: report.reportName }))
		},
		currentReport() {
			return this.reports.find(report => report.reportKey === this.activeReportKey) || null
		},
		currentItems() {
			return this.currentReport ? (this.currentReport.items || []) : []
		},
		showLastYear() {
			return this.currentReport && this.currentReport.reportType === 3
		},
		displayAccountColumns() {
			return this.includeAccountColumns ? this.selectedAccountSets : []
		},
		eliminationRows() {
			const rows = []
			;(this.reports || []).forEach(report => {
				;(report.items || []).forEach(item => {
					const currentYearBefore = this.calcBeforeAmount(item.currentYearAmount, item.eliminationCurrentYearAmount)
					const currentPeriodBefore = this.calcBeforeAmount(item.currentPeriodAmount, item.eliminationCurrentPeriodAmount)
					const lastYearBefore = this.calcBeforeAmount(item.lastYearAmount, item.eliminationLastYearAmount)
					rows.push({
						reportKey: report.reportKey,
						reportName: report.reportName,
						lineKey: item.lineKey,
						title: item.title,
						level: item.level || 1,
						lineNum: item.isClassified ? '' : (item.lineNum > -1 ? item.lineNum : ''),
						currentYearBefore,
						currentYearElimination: item.eliminationCurrentYearAmount,
						currentPeriodBefore,
						currentPeriodElimination: item.eliminationCurrentPeriodAmount,
						lastYearBefore,
						lastYearElimination: item.eliminationLastYearAmount
					})
				})
			})
			return rows
		},
		filteredEliminationRows() {
			const keyword = (this.eliminationModal.keyword || '').trim()
			return (this.eliminationRows || []).filter(item => {
				if (this.eliminationModal.reportKey && item.reportKey !== this.eliminationModal.reportKey) {
					return false
				}
				if (keyword && (!item.title || item.title.indexOf(keyword) < 0)) {
					return false
				}
				if (this.eliminationModal.onlyWithElimination) {
					const year = this.getEliminationValueByKey(item.reportKey, item.lineKey, 'currentYearAmount', item.currentYearElimination)
					const period = this.getEliminationValueByKey(item.reportKey, item.lineKey, 'currentPeriodAmount', item.currentPeriodElimination)
					const lastYear = this.getEliminationValueByKey(item.reportKey, item.lineKey, 'lastYearAmount', item.lastYearElimination)
					return this.hasNonZero(year) || this.hasNonZero(period) || this.hasNonZero(lastYear)
				}
				return true
			})
		},
		eliminationHasLastYear() {
			return (this.filteredEliminationRows || []).some(item => item.lastYearBefore != null || this.hasNonZero(item.lastYearElimination))
		}
	},
	watch: {
		selectedAccountSetsIds(newVal) {
			if (!newVal || newVal.length === 0) {
				this.anchorAccountSetsId = null
				return
			}
			if (!newVal.includes(this.anchorAccountSetsId)) {
				this.anchorAccountSetsId = newVal[0]
			}
		}
	},
		methods: {
		openEliminationModal() {
			if (!this.reports || this.reports.length === 0) {
				this.loadPreview()
			}
			this.eliminationModal.visible = true
		},
		loadAccountSets() {
			this.loading = true
			this.$api.report.merge.accountSets().then(({ data }) => {
				this.availableAccountSets = data || []
				const defaults = this.availableAccountSets.slice(0, 2).map(item => item.id)
				this.selectedAccountSetsIds = defaults.length > 0 ? defaults : []
				this.anchorAccountSetsId = this.selectedAccountSetsIds.length > 0 ? this.selectedAccountSetsIds[0] : null
			}).finally(() => {
				this.loading = false
			})
		},
			loadPreview() {
			if (!this.accountDate) {
				this.$Message.error('请选择会计期间')
				return
			}
			if (!this.selectedAccountSetsIds || this.selectedAccountSetsIds.length < 2) {
				this.$Message.error('请至少选择2个账套')
				return
			}
			this.previewLoading = true
			this.$api.report.merge.preview({
				accountDate: this.accountDate,
				accountSetsIds: this.selectedAccountSetsIds,
				anchorAccountSetsId: this.anchorAccountSetsId,
				eliminations: this.buildEliminations()
			}).then(({ data }) => {
				this.reports = data.reports || []
				this.warnings = data.warnings || []
				if (!this.activeReportKey || !this.reports.find(report => report.reportKey === this.activeReportKey)) {
					this.activeReportKey = this.reports.length > 0 ? this.reports[0].reportKey : ''
				}
				this.syncEliminationMapFromReports()
				}).finally(() => {
					this.previewLoading = false
				})
			},
			aiSuggestEliminations() {
				if (!this.accountDate) {
					this.$Message.error('请选择会计期间')
					return
				}
				if (!this.selectedAccountSetsIds || this.selectedAccountSetsIds.length < 2) {
					this.$Message.error('请至少选择2个账套')
					return
				}
				this.aiLoading = true
				this.$api.report.merge.aiEliminations({
					accountDate: this.accountDate,
					accountSetsIds: this.selectedAccountSetsIds,
					anchorAccountSetsId: this.anchorAccountSetsId,
					eliminations: this.buildEliminations()
				}).then(({ data }) => {
					const suggestions = (data && data.eliminations) || []
					if (suggestions.length === 0) {
						this.$Message.info((data && data.message) || 'AI未识别到可用抵消建议')
						return
					}
					suggestions.forEach(item => {
						if (!item || !item.reportKey || !item.lineKey) {
							return
						}
						const key = this.getEliminationKey(item.reportKey, item.lineKey)
						const old = this.eliminationMap[key] || {
							reportKey: item.reportKey,
							lineKey: item.lineKey,
							currentPeriodAmount: null,
							currentYearAmount: null,
							lastYearAmount: null
						}
						old.currentPeriodAmount = item.currentPeriodAmount
						old.currentYearAmount = item.currentYearAmount
						old.lastYearAmount = item.lastYearAmount
						this.$set(this.eliminationMap, key, old)
					})

					if (data && data.warnings && data.warnings.length > 0) {
						this.warnings = [...new Set([...(this.warnings || []), ...data.warnings])]
					}

					this.$Message.success(`AI已生成${suggestions.length}条抵消建议`)
					this.loadPreview()
				}).catch((e) => {
					console.error('AI抵消建议失败', e)
					this.$Message.error('AI抵消建议失败，请检查模型配置')
				}).finally(() => {
					this.aiLoading = false
				})
			},
			exportExcel() {
				if (!this.accountDate) {
					this.$Message.error('请选择会计期间')
					return
				}
				if (!this.selectedAccountSetsIds || this.selectedAccountSetsIds.length < 2) {
					this.$Message.error('请至少选择2个账套')
					return
				}
				this.exportLoading = true
				this.$api.report.merge.export({
					accountDate: this.accountDate,
					accountSetsIds: this.selectedAccountSetsIds,
					anchorAccountSetsId: this.anchorAccountSetsId,
					eliminations: this.buildEliminations(),
					includeAccountColumns: this.includeAccountColumns
				}).then(() => {
					this.$Message.success('合并报表导出成功')
				}).catch((e) => {
					console.error('合并报表导出失败', e)
					this.$Message.error('导出失败，请重试')
				}).finally(() => {
					this.exportLoading = false
				})
			},
			buildEliminations() {
			const list = []
			Object.keys(this.eliminationMap).forEach(key => {
				const item = this.eliminationMap[key]
				if (!item) {
					return
				}
				if (item.currentPeriodAmount == null && item.currentYearAmount == null && item.lastYearAmount == null) {
					return
				}
				list.push({
					reportKey: item.reportKey,
					lineKey: item.lineKey,
					currentPeriodAmount: item.currentPeriodAmount,
					currentYearAmount: item.currentYearAmount,
					lastYearAmount: item.lastYearAmount
				})
			})
			return list
		},
		syncEliminationMapFromReports() {
			const map = {}
			;(this.reports || []).forEach(report => {
				;(report.items || []).forEach(item => {
					const hasElimination = item.eliminationCurrentPeriodAmount != null
						|| item.eliminationCurrentYearAmount != null
						|| item.eliminationLastYearAmount != null
					if (!hasElimination) {
						return
					}
					const key = this.getEliminationKey(report.reportKey, item.lineKey)
					map[key] = {
						reportKey: report.reportKey,
						lineKey: item.lineKey,
						currentPeriodAmount: item.eliminationCurrentPeriodAmount,
						currentYearAmount: item.eliminationCurrentYearAmount,
						lastYearAmount: item.eliminationLastYearAmount
					}
				})
			})
			Object.keys(this.eliminationMap).forEach(key => {
				if (!map[key]) {
					map[key] = this.eliminationMap[key]
				}
			})
			this.eliminationMap = map
		},
		getEliminationKey(reportKey, lineKey) {
			return `${reportKey}#${lineKey}`
		},
		getBreakdownAmount(row, accountSetsId, field) {
			if (!row || !row.breakdown || !Array.isArray(row.breakdown)) {
				return null
			}
			const item = row.breakdown.find(v => v.accountSetsId === accountSetsId)
			if (!item) {
				return null
			}
			return item[field]
		},
		getEliminationValue(row, field) {
			if (!this.currentReport) {
				return null
			}
			return this.getEliminationValueByKey(this.currentReport.reportKey, row.lineKey, field, null)
		},
		getEliminationValueByKey(reportKey, lineKey, field, defaultValue = null) {
			const key = this.getEliminationKey(reportKey, lineKey)
			const elimination = this.eliminationMap[key]
			return elimination ? elimination[field] : defaultValue
		},
		setEliminationValue(row, field, rawValue) {
			if (!this.currentReport) {
				return
			}
			this.setEliminationValueByKey(this.currentReport.reportKey, row.lineKey, field, rawValue)
		},
		setEliminationValueByKey(reportKey, lineKey, field, rawValue) {
			const key = this.getEliminationKey(reportKey, lineKey)
			const value = this.parseNumber(rawValue)
			const elimination = this.eliminationMap[key] || {
				reportKey,
				lineKey,
				currentPeriodAmount: null,
				currentYearAmount: null,
				lastYearAmount: null
			}
			elimination[field] = value

			if (elimination.currentPeriodAmount == null
				&& elimination.currentYearAmount == null
				&& elimination.lastYearAmount == null) {
				if (this.hasServerElimination(reportKey, lineKey)) {
					this.$set(this.eliminationMap, key, elimination)
				} else {
					this.$delete(this.eliminationMap, key)
				}
			} else {
				this.$set(this.eliminationMap, key, elimination)
			}
		},
		hasServerElimination(reportKey, lineKey) {
			const line = this.findServerLine(reportKey, lineKey)
			if (!line) {
				return false
			}
			return line.eliminationCurrentPeriodAmount != null
				|| line.eliminationCurrentYearAmount != null
				|| line.eliminationLastYearAmount != null
		},
		findServerLine(reportKey, lineKey) {
			const report = (this.reports || []).find(item => item.reportKey === reportKey)
			if (!report || !report.items) {
				return null
			}
			return report.items.find(item => item.lineKey === lineKey) || null
		},
		hasNonZero(value) {
			if (value == null) {
				return false
			}
			return Math.abs(Number(value)) > 0.0000001
		},
		calcBeforeAmount(afterAmount, eliminationAmount) {
			if (afterAmount == null && eliminationAmount == null) {
				return null
			}
			return (afterAmount || 0) + (eliminationAmount || 0)
		},
		calcAfterAmount(beforeAmount, reportKey, lineKey, field, fallbackElimination) {
			if (beforeAmount == null) {
				return null
			}
			const elimination = this.getEliminationValueByKey(reportKey, lineKey, field, fallbackElimination)
			return (beforeAmount || 0) - (elimination || 0)
		},
		parseNumber(value) {
			if (value === '' || value == null) {
				return null
			}
			const num = Number(value)
			if (isNaN(num)) {
				return null
			}
			return num
		},
		formatInput(value) {
			return value == null ? '' : value
		}
	},
	mounted() {
		if (this.currentAccountSets && this.currentAccountSets.currentAccountDate) {
			this.accountDate = this.currentAccountSets.currentAccountDate
		}
		this.loadAccountSets()
	}
}
</script>

<style scoped lang="less">
.margin-right {
	margin-right: 10px;
}
.option-panel {
	border: 1px solid #dfe5ef;
	padding: 12px 14px;
	border-radius: 8px;
	background: #ffffff;
}
.option-inline {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 16px;
	flex-wrap: wrap;
}
.option-block {
	display: flex;
	flex-direction: column;
}
.option-title {
	font-weight: bold;
	margin-bottom: 6px;
	color: #2b3a4a;
}
.account-sets-list {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
	gap: 8px 10px;
}
.account-item {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 6px 8px;
	border-radius: 6px;
	background: #f8fafc;
	border: 1px solid #edf1f7;
}
.anchor-select {
	width: 360px;
}
.option-check {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 8px 10px;
	border-radius: 6px;
	background: #f8fafc;
	border: 1px solid #edf1f7;
	margin-bottom: 2px;
}
.warn-box {
	background: #fff8e1;
	border: 1px solid #ffe082;
	padding: 10px;
	border-radius: 4px;
}
.warn-title {
	font-weight: bold;
	margin-bottom: 6px;
}
.warn-item {
	line-height: 22px;
}
.cell-input {
	width: 120px;
	text-align: right;
	border: 1px solid #dcdfe6;
	border-radius: 2px;
	padding: 2px 6px;
}
.breakdown-item {
	line-height: 20px;
	white-space: nowrap;
}
.account-column-cell {
	line-height: 20px;
	white-space: nowrap;
}
.elimination-toolbar {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 10px;
	flex-wrap: wrap;
}
.elimination-filter {
	display: inline-flex;
	align-items: center;
	gap: 6px;
}
.elimination-filter label {
	color: #2b3a4a;
	font-weight: bold;
}
.elimination-select {
	width: 180px;
}
.elimination-input {
	width: 240px;
}
.elimination-check {
	margin-left: auto;
}
</style>
